/**
 * overlaySurfaceRegistry
 *
 * A typed registry mapping URL-friendly surface names to the metadata
 * needed to (de)serialize them in the address bar AND to open them via
 * the OverlayOrchestrator.
 *
 * This is the single source of truth for:
 *   - which surfaces support deep-link restoration on cold load
 *   - how URL params ↔ SurfaceId conversion works
 *   - the canonical query-string keys (`overlay`, `entityId`, `tab`)
 *
 * Adding a new deep-linkable drawer should be a one-line registration here,
 * not a scattered `if (params.get('foo'))` block in another file.
 *
 * Phase-2 unification scope:
 *   - Canonical key `overlay` is preferred for new code.
 *   - Legacy investor `panel` key is recognised as an alias (read-only) so
 *     existing `?panel=milestone-review&entityId=...` URLs continue to
 *     resolve until investor flows are fully migrated.
 *
 * @module lib/navigation/overlaySurfaceRegistry
 */

import type { SurfaceId } from './types';

// ── Canonical URL parameter keys ─────────────────────────────────────────
export const OVERLAY_PARAM = 'overlay';
export const ENTITY_ID_PARAM = 'entityId';
export const TAB_PARAM = 'tab';
export const LEGACY_PANEL_PARAM = 'panel';

// ── Restoration descriptor (parsed URL → orchestrator open params) ───────
export interface OverlaySurfaceDescriptor {
    /** Stable URL key (kebab-case, lowercase). Matches the `overlay` query param. */
    name: string;
    /** SurfaceId built from URL params — fed to orchestrator.openOverlay. */
    surface: SurfaceId;
    /** Optional tab/sub-section captured from URL. */
    tab?: string;
    /** Optional entity id captured from URL. */
    entityId?: string;
}

// ── Registry entry ───────────────────────────────────────────────────────
export interface OverlaySurfaceRegistryEntry {
    /** URL key (e.g. `gate-detail-drawer`). */
    name: string;
    /**
     * Whether the orchestrator can restore this surface from a cold-loaded
     * URL on its own. False for surfaces whose renderer is mounted on a
     * specific page only (we still recognise the URL params, but we trust
     * the page-local host to render them).
     */
    canRestore: boolean;
    /**
     * Build a SurfaceId from raw URL params. Return `null` to signal that
     * the params are malformed and the URL entry should be cleared.
     */
    parseUrlParams: (params: URLSearchParams) => SurfaceId | null;
    /**
     * Reverse direction: given an open orchestrator entry (with surface +
     * optional `tab` prop), produce the URLSearchParams patch that
     * represents it. Implementations should ONLY set keys they own; the
     * sync hook will compose patches across keys.
     */
    serializeToUrl: (input: { surface: SurfaceId; tab?: string }) => Record<string, string | undefined>;
    /**
     * Marks this entry as a legacy alias driven by the investor `panel`
     * key. The sync hook will not write this key when serializing — the
     * canonical `overlay` key is used instead.
     */
    legacy?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Standard `entityId`-driven URL parser. */
function parseEntityIdSurface(
    surfaceName: string,
    entityType: string,
    surfaceKind: SurfaceId['kind'] = 'drawer',
): OverlaySurfaceRegistryEntry['parseUrlParams'] {
    return (params) => {
        const entityId = params.get(ENTITY_ID_PARAM);
        if (!entityId) return null;
        return { kind: surfaceKind, name: surfaceName, entityType, entityId };
    };
}

function serializeEntityIdSurface(name: string): OverlaySurfaceRegistryEntry['serializeToUrl'] {
    return ({ surface, tab }) => {
        const patch: Record<string, string | undefined> = {
            [OVERLAY_PARAM]: name,
            [ENTITY_ID_PARAM]: surface.entityId,
        };
        // Only write TAB_PARAM when the overlay explicitly owns a tab value.
        // If tab is undefined the page-level ?tab= param (e.g. ?tab=capital)
        // must not be touched — it belongs to the host page, not this overlay.
        if (tab !== undefined) patch[TAB_PARAM] = tab;
        return patch;
    };
}

// ── The registry ─────────────────────────────────────────────────────────
//
// Canonical entries (preferred for new code).
// Add new deep-linkable drawers here.
const CANONICAL_ENTRIES: OverlaySurfaceRegistryEntry[] = [
    {
        name: 'gate-detail-drawer',
        canRestore: true,
        parseUrlParams: parseEntityIdSurface('gate-detail-drawer', 'gate'),
        serializeToUrl: serializeEntityIdSurface('gate-detail-drawer'),
    },
    {
        name: 'milestone-drawer',
        canRestore: true,
        parseUrlParams: parseEntityIdSurface('milestone-drawer', 'milestone'),
        serializeToUrl: serializeEntityIdSurface('milestone-drawer'),
    },
    {
        name: 'document-inspector',
        canRestore: true,
        parseUrlParams: parseEntityIdSurface('document-inspector', 'document'),
        serializeToUrl: serializeEntityIdSurface('document-inspector'),
    },
];

// Legacy investor `?panel=...` aliases. These are READ-ONLY: we recognise
// them on cold load so existing notification deep-links keep working, but
// the sync hook never emits the `panel` key. New URLs should use `overlay`.
//
// Each maps onto a SurfaceId with kind:'drawer' and name='investor-<panel>'.
const LEGACY_PANEL_ENTRIES: ReadonlyArray<{
    panel: string;          // value of ?panel=
    surfaceName: string;    // SurfaceId.name
    entityType: string;     // SurfaceId.entityType
}> = [
        { panel: 'position-detail', surfaceName: 'investor-position-detail', entityType: 'investment' },
        { panel: 'milestone-review', surfaceName: 'investor-milestone-review', entityType: 'milestone' },
        { panel: 'approval-review', surfaceName: 'investor-approval-review', entityType: 'approval_workflow' },
        { panel: 'signature-review', surfaceName: 'investor-signature-review', entityType: 'signature_request' },
        { panel: 'gate-review', surfaceName: 'investor-gate-review', entityType: 'gate' },
        { panel: 'finance-review', surfaceName: 'investor-finance-review', entityType: 'finance_approval' },
        { panel: 'evidence-preview', surfaceName: 'investor-evidence-preview', entityType: 'evidence_pack' },
        { panel: 'document-preview', surfaceName: 'investor-document-preview', entityType: 'document' },
        { panel: 'distribution-detail', surfaceName: 'investor-distribution-detail', entityType: 'distribution' },
    ];

const LEGACY_ENTRIES: OverlaySurfaceRegistryEntry[] = LEGACY_PANEL_ENTRIES.map((meta) => ({
    name: meta.surfaceName,
    canRestore: false, // The InvestorDrawerHost mounts and renders these from `panel`/`entityId` itself.
    legacy: true,
    parseUrlParams: parseEntityIdSurface(meta.surfaceName, meta.entityType),
    // Legacy investor surfaces are owned by `useInvestorPanelState` — it
    // writes the `?panel=` key. The unified overlay sync MUST NOT also
    // write a `?overlay=` for them or the URL doubles up. Returning an
    // empty patch makes serialization a no-op for legacy surfaces.
    serializeToUrl: () => ({}),
}));

const ALL_ENTRIES: ReadonlyArray<OverlaySurfaceRegistryEntry> = [...CANONICAL_ENTRIES, ...LEGACY_ENTRIES];

const BY_NAME: ReadonlyMap<string, OverlaySurfaceRegistryEntry> = new Map(
    ALL_ENTRIES.map((e) => [e.name, e]),
);

const LEGACY_PANEL_TO_SURFACE: ReadonlyMap<string, string> = new Map(
    LEGACY_PANEL_ENTRIES.map((m) => [m.panel, m.surfaceName]),
);

// ── Public API ───────────────────────────────────────────────────────────

/** Look up a registry entry by surface name, or `undefined` if unknown. */
export function getOverlaySurfaceEntry(name: string): OverlaySurfaceRegistryEntry | undefined {
    return BY_NAME.get(name);
}

/**
 * Parse the current URL search params into a restoration descriptor, if a
 * recognised overlay surface is present. Honours both the canonical
 * `?overlay=` key and the legacy investor `?panel=` alias.
 *
 * Returns `null` when no overlay is requested OR when the requested
 * surface is unknown (caller may then clear the offending params).
 */
export function parseOverlayFromUrl(params: URLSearchParams): OverlaySurfaceDescriptor | null {
    // Canonical key wins if both are set.
    const canonical = params.get(OVERLAY_PARAM);
    if (canonical) {
        const entry = BY_NAME.get(canonical);
        if (!entry) return null;
        const surface = entry.parseUrlParams(params);
        if (!surface) return null;
        return {
            name: entry.name,
            surface,
            tab: params.get(TAB_PARAM) ?? undefined,
            entityId: surface.entityId,
        };
    }

    const legacyPanel = params.get(LEGACY_PANEL_PARAM);
    if (legacyPanel) {
        const surfaceName = LEGACY_PANEL_TO_SURFACE.get(legacyPanel);
        if (!surfaceName) return null;
        const entry = BY_NAME.get(surfaceName);
        if (!entry) return null;
        const surface = entry.parseUrlParams(params);
        if (!surface) return null;
        return {
            name: entry.name,
            surface,
            tab: params.get(TAB_PARAM) ?? undefined,
            entityId: surface.entityId,
        };
    }

    return null;
}

/**
 * Serialize an open overlay surface to URL search-param patches, OR
 * return a patch that clears overlay-related keys when no overlay is
 * supplied.
 *
 * Caller is expected to merge this patch into a fresh URLSearchParams.
 */
export function serializeOverlayToUrl(input: {
    surface: SurfaceId;
    tab?: string;
} | null): Record<string, string | undefined> {
    if (!input) {
        // Clear overlay-owned keys only.
        // TAB_PARAM (?tab=) is intentionally NOT cleared here — it is owned
        // by the host page (e.g. ?tab=capital in ProjectDashboard), not by
        // the overlay. Clearing it would reset the host page's active tab
        // to its default whenever any drawer closes.
        return {
            [OVERLAY_PARAM]: undefined,
            [ENTITY_ID_PARAM]: undefined,
            [LEGACY_PANEL_PARAM]: undefined,
        };
    }
    const entry = BY_NAME.get(input.surface.name);
    if (!entry) {
        // Unknown surface → don't touch URL.
        return {};
    }
    return entry.serializeToUrl(input);
}

/** True if this surface should be restored on cold load by the URL sync hook. */
export function canRestoreFromUrl(name: string): boolean {
    return BY_NAME.get(name)?.canRestore === true;
}

/** Test-only: enumerate registered names. */
export function listRegisteredOverlaySurfaces(): readonly string[] {
    return Array.from(BY_NAME.keys());
}
